// "Download Zones" asks the server for the file. Smoothing, clipping, and the
// report run there, on the zones the screen is already showing.
import type { ManifestLayer } from "../types/contract";
import type { ZoneExportFormat } from "./geoFormats";
import { postJsonForFile } from "../api/httpGateway";

async function layerFc(
  layer: ManifestLayer,
): Promise<GeoJSON.FeatureCollection> {
  let geo: unknown = layer.inline;
  if (!geo && layer.signed_url) {
    const res = await fetch(layer.signed_url);
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    geo = await res.json();
  }
  if (!geo) throw new Error("This zone layer isn’t available to download yet.");
  return geo as GeoJSON.FeatureCollection;
}

function localDate(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function filenameOf(header: string | null, fallback: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header ?? "");
  if (encoded?.[1]) return decodeURIComponent(encoded[1]);
  const plain = /filename="([^"]+)"/i.exec(header ?? "");
  return plain?.[1] || fallback;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function saveZonesFile(
  fieldName: string,
  fc: GeoJSON.FeatureCollection,
  boundary: unknown | null,
  format: ZoneExportFormat,
): Promise<void> {
  const { blob, disposition } = await postJsonForFile("/exports/zones", {
    field_name: fieldName || "Field",
    format,
    zones: fc,
    boundary,
    // The report date is the user's calendar day, not the server's UTC day.
    local_date: localDate(),
  });
  triggerDownload(blob, filenameOf(disposition, "zones"));
}

/* * The map-tab export: fetch the visible zone layer + boundary, then the server writes the file. */
export async function downloadZones(
  fieldName: string,
  zoneLayer: ManifestLayer,
  boundaryLayer: ManifestLayer | null,
  format: ZoneExportFormat = "shp",
): Promise<void> {
  const fc = await layerFc(zoneLayer);
  const boundary = boundaryLayer
    ? await layerFc(boundaryLayer).catch(() => null)
    : null;
  await saveZonesFile(fieldName, fc, boundary, format);
}

/*
 *
 * "Save & Download" from the sampling-confirm dialog: export a zone design the caller already HOLDS
 * (a customize commit, a preset default) without a manifest round-trip for the zones — only the
 * boundary comes from the manifest, for the exact clip; unreachable boundary degrades to no clip.
 */
export async function downloadDesignedZones(
  field: { field_id: string; field_name: string },
  fc: GeoJSON.FeatureCollection,
  format: ZoneExportFormat = "shp",
): Promise<void> {
  const { api } = await import("../api");
  const listed = await api
    .fetchFieldLayerList(field.field_id)
    .catch(() => null);
  const boundary = listed?.document?.boundary
    ? await layerFc({
        inline: listed.document.boundary,
      } as ManifestLayer).catch(() => null)
    : null;
  await saveZonesFile(field.field_name, fc, boundary, format);
}

/*
 *
 * Ensure every zone feature carries `zone_name` — the name the user SAW on screen. Precedence: an
 * existing zone_name (fresh saves stamp it at the dissolve) → the class `code` (decile/behavior zones) →
 * the zone number → the 1-based feature order. Shared by the SAVE path and the DOWNLOAD path so layers
 * saved before this existed, and compute-served automaps, still export named.
 */
export function withZoneNames(
  fc: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  return {
    ...fc,
    features: fc.features.map((f, i) => {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      if (p.zone_name != null) return f;
      const name =
        p.code != null
          ? String(p.code)
          : p.zone_number != null
            ? String(p.zone_number)
            : p.zone != null
              ? String(p.zone)
              : String(i + 1);
      return { ...f, properties: { ...p, zone_name: name } };
    }),
  };
}

/* * Which visible manifest layer counts as "the zone layer" for the button. */
export function visibleZoneLayer(
  layers: ManifestLayer[],
  isVisible: (l: ManifestLayer) => boolean,
): ManifestLayer | null {
  const isZones = (l: ManifestLayer) => {
    const id = l.id ?? "";
    return (
      id === "zones" ||
      (id.startsWith("automap-") && id.includes("zones")) ||
      id.startsWith("target-zones-")
    );
  };
  return layers.find((l) => isZones(l) && isVisible(l)) ?? null;
}
